import { createAsyncThunk } from '@reduxjs/toolkit';

import { setLoading } from 'src/components/common/loading/loadingSlice';
import { annotationTemplateApi } from 'src/features/annotation_template/infrastructure/api';
import {
	ICreateQuestionnaireTemplateThunkArgs,
	IGetQuestionnaireTemplateThunkArgs,
	IQuestionnaireTemplateResponse,
	IQuestionnaireTemplate,
	IGetQuestionnaireTemplatesThunkArgs,
} from 'src/features/questionnaire_template/domain/types';
import { handleThunkError } from 'src/utils/handleThunkError';

export const ANNOTATION_TEMPLATE_LOADING_KEY = 'annotation_template';

interface IDeleteAnnotationTemplateThunkArgs {
	annotationTemplate: IQuestionnaireTemplate;
	siteSlug: string;
	onSuccess?: () => void;
	onError?: (err: IErrorResponse) => void;
}

export const createAnnotationTemplate = createAsyncThunk<
	IQuestionnaireTemplateResponse,
	ICreateQuestionnaireTemplateThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'annotation_template/createAnnotationTemplate',
	async (
		{ requestBody, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: ANNOTATION_TEMPLATE_LOADING_KEY,
					loading: true,
				}),
			);
			const response =
				await annotationTemplateApi.createAnnotationTemplate(
					requestBody,
					siteSlug,
				);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to create annotation template',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: ANNOTATION_TEMPLATE_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);

export const getAnnotationTemplate = createAsyncThunk<
	IQuestionnaireTemplateResponse,
	IGetQuestionnaireTemplateThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'annotation_template/getAnnotationTemplate',
	async (
		{ questionnaireTemplateId, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: ANNOTATION_TEMPLATE_LOADING_KEY,
					loading: true,
				}),
			);
			const response = await annotationTemplateApi.getAnnotationTemplate(
				questionnaireTemplateId,
				siteSlug,
			);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to fetch annotation template',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: ANNOTATION_TEMPLATE_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);

export const getAnnotationTemplates = createAsyncThunk<
	IQuestionnaireTemplate[],
	IGetQuestionnaireTemplatesThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'annotation_template/getAnnotationTemplates',
	async (
		{ requestParams, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: ANNOTATION_TEMPLATE_LOADING_KEY,
					loading: true,
				}),
			);
			const response = await annotationTemplateApi.getAnnotationTemplates(
				requestParams,
				siteSlug,
			);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to fetch annotation templates',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: ANNOTATION_TEMPLATE_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);

export const deleteAnnotationTemplate = createAsyncThunk<
	void,
	IDeleteAnnotationTemplateThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'annotation_template/deleteAnnotationTemplate',
	async (
		{ annotationTemplate, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: ANNOTATION_TEMPLATE_LOADING_KEY,
					loading: true,
				}),
			);
			await annotationTemplateApi.deleteAnnotationTemplate(
				annotationTemplate,
				siteSlug,
			);

			onSuccess?.();
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to delete annotation template',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: ANNOTATION_TEMPLATE_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);
